import { LucaIcon } from '@components'
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react'
import { useOpenLucaWithPrompt } from '@hooks'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowRight } from 'react-icons/fa'
import styled, { keyframes } from 'styled-components'

export interface AskLucaProps {
  prompts: string[]
  onNavy?: boolean
  placement?: Placement
  className?: string
}

export const AskLuca: React.FC<AskLucaProps> = ({
  prompts,
  onNavy,
  placement = 'bottom-end',
  className
}) => {
  const [t] = useTranslation()
  const [isOpen, setOpen] = useState(false)
  const openLucaWithPrompt = useOpenLucaWithPrompt()

  const { context, refs, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { delay: { open: 50, close: 120 }, move: false }),
    useClick(context),
    useDismiss(context),
    useRole(context, { role: 'menu' })
  ])

  const ask = (prompt: string) => {
    setOpen(false)
    openLucaWithPrompt(prompt)
  }

  return (
    <>
      <Mark
        type="button"
        ref={refs.setReference}
        className={className}
        $onNavy={onNavy}
        aria-label={t('dashboard.askLuca.ariaLabel')}
        aria-expanded={isOpen}
        {...getReferenceProps()}
      >
        <LucaIcon size={17} />
      </Mark>

      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <PromptList
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              key="ask-luca-prompts"
            >
              <PromptListTitle>{t('dashboard.askLuca.title')}</PromptListTitle>
              {prompts.map(prompt => (
                <PromptRow key={prompt} type="button" onClick={() => ask(prompt)}>
                  <span>{prompt}</span>
                  <FaArrowRight />
                </PromptRow>
              ))}
            </PromptList>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </>
  )
}

/* Same slow spin as the widget's thinking mark (`.ai-thinking-loader__mark`). */
const lucaSpin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

const Mark = styled.button<{ $onNavy?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: 0.8;
  color: ${({ theme, $onNavy }) => ($onNavy ? theme.luca.sky : theme.colors.primary)};
  transition: transform 180ms ease-out, color 180ms ease-out, opacity 180ms ease-out,
    background 180ms ease-out;

  svg {
    display: block;
    transform-origin: 50% 50%;
    transform: rotate(0deg);
    transition: transform 180ms ease-out;
  }

  &:hover,
  &[aria-expanded='true'] {
    opacity: 1;
  }

  &[aria-expanded='true'] svg {
    animation: ${lucaSpin} 9s linear infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    svg {
      transition: none;
    }

    &[aria-expanded='true'] svg {
      animation: none;
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  }
`

const PromptList = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.12, type: 'tween' }
})`
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: max-content;
  max-width: 18.125rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.menuBg};
  box-shadow: 0 1px 5px 2px rgba(0, 0, 0, 0.14);
  outline: none;
`

const PromptListTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.px10}rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.luca.sky};
`

const PromptRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: rgba(255, 255, 255, 0.08);
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  line-height: 1.35;
  text-align: left;
  text-wrap: pretty;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  svg {
    flex-shrink: 0;
    margin-left: auto;
    font-size: ${({ theme }) => theme.fontSize.xxs}rem;
    color: ${({ theme }) => theme.luca.sky};
  }
`
